/**
 * The overdue-invoice feed behind the CRM-wide warning banner.
 *
 * One deliberately small read (`GET /invoices?status=overdue`) plus the two
 * lifecycle writes the banner's drawer needs (mark paid / undo). It is kept OUT
 * of `store/invoicesSlice` on purpose: the banner mounts on EVERY CRM page, and
 * writing into the shared invoices slice from the layout would clobber whatever
 * filtered list the Invoices page itself is showing.
 *
 * `status=overdue` is the coarse server-side lifecycle filter
 * (crm-api Invoice scope: `due_date < today AND payment_status != 'paid'`), so
 * "overdue" is computed by the database rather than guessed client-side, and the
 * rows come back already scoped to what the actor may see
 * (`User#scope_money_documents` — own records, or all under `crm_view_all`).
 *
 * Money note: every amount this module returns is rendered through the financial
 * wall (`components/money`). The feed is only ever fetched for a viewer holding
 * BOTH `invoices:access` and `financials:access` — see `useOverdueInvoices`.
 */
import type { OverdueTotals } from '@xop/dashboard-shared'

import api from './api'

/** Severity + its thresholds live in `@xop/dashboard-shared` so the CRM bar,
 * the dashboard server's summary endpoint, and the top-nav alert dot can never
 * disagree about what "critical" means. Re-exported here because this module
 * stays the CRM's one door to the overdue feed. */
export {
  CRITICAL_DAYS_OVERDUE,
  CRITICAL_INVOICE_COUNT,
  overdueSeverity,
  type OverdueSeverity,
} from '@xop/dashboard-shared'

/** An amount as crm-api actually sends it. Rails serializes these BigDecimal
 * columns as JSON STRINGS ("1250.0"), not numbers — verified live against
 * `GET /api/v1/invoices`. Typing them `number` would be a lie that invites
 * `a + b` string concatenation downstream, so every read goes through
 * `amountOutstanding` (which coerces). */
export type MoneyField = number | string | null | undefined

/** The subset of the invoice serializer the banner + drawer actually read. */
export interface OverdueInvoice {
  id: number
  invoice_number: string
  customer_id?: number
  customer_name?: string
  customer_company_name?: string
  /** ISO8601 (crm-api's BaseSerializer#format_date). */
  due_date: string
  total_amount: MoneyField
  balance_due?: MoneyField
  /** Server-computed; falls back to a local diff when an older API omits it. */
  days_overdue?: number
  payment_status?: string
}

/** What the banner renders from: count + outstanding + age of the oldest debt.
 * Structurally the shared `OverdueTotals` — the same shape the nav alert dot
 * and the server's summary endpoint speak. */
export type OverdueSummary = OverdueTotals

/** Cap on how many rows the drawer loads. The banner's COUNT comes from the
 * same request's pagination total, so a tenant with more than this still sees a
 * truthful headline number — only the list is capped. */
export const OVERDUE_FETCH_LIMIT = 100

const MS_PER_DAY = 24 * 60 * 60 * 1000

/**
 * Days an invoice is past its due date. Prefers the server's `days_overdue`
 * (computed against the API's clock, which owns the definition) and falls back
 * to a UTC-midnight diff so a browser in a different timezone can't render
 * "-1 days late" for something due today.
 */
export function daysOverdue(invoice: OverdueInvoice, now: Date = new Date()): number {
  if (typeof invoice.days_overdue === 'number' && Number.isFinite(invoice.days_overdue)) {
    return Math.max(0, Math.trunc(invoice.days_overdue))
  }
  const due = Date.parse(invoice.due_date)
  if (Number.isNaN(due)) return 0
  const dueDay = Math.floor(due / MS_PER_DAY)
  const today = Math.floor(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) / MS_PER_DAY)
  return Math.max(0, today - dueDay)
}

/** What is still owed on an invoice — the balance when the API reports one
 * (partial payments), otherwise the full amount. */
export function amountOutstanding(invoice: OverdueInvoice): number {
  const balance = Number(invoice.balance_due)
  if (Number.isFinite(balance) && balance > 0) return balance
  const total = Number(invoice.total_amount)
  return Number.isFinite(total) ? total : 0
}

/**
 * Roll a list of overdue invoices into the headline the banner shows. Pure so
 * the banner's copy is unit-testable without HTTP or React.
 *
 * `count` may be passed separately when the server reported more rows than the
 * fetch limit returned — the headline must not under-report.
 */
export function summarizeOverdue(
  invoices: OverdueInvoice[],
  totalCount?: number,
  now: Date = new Date(),
): OverdueSummary {
  return {
    count: typeof totalCount === 'number' && totalCount > invoices.length ? totalCount : invoices.length,
    totalAmount: invoices.reduce((sum, inv) => sum + amountOutstanding(inv), 0),
    maxDaysOverdue: invoices.reduce((max, inv) => Math.max(max, daysOverdue(inv, now)), 0),
  }
}

/** Oldest-debt-first: most days overdue, then largest amount. The order the
 * drawer lists them in — you chase the worst one first. */
export function sortByUrgency(invoices: OverdueInvoice[], now: Date = new Date()): OverdueInvoice[] {
  return [...invoices].sort((a, b) => {
    const byAge = daysOverdue(b, now) - daysOverdue(a, now)
    if (byAge !== 0) return byAge
    return amountOutstanding(b) - amountOutstanding(a)
  })
}

/** Display name for the "who owes this" column. */
export function overdueCustomerLabel(invoice: OverdueInvoice): string {
  return invoice.customer_company_name || invoice.customer_name || 'Unknown customer'
}

export interface OverdueFetchResult {
  invoices: OverdueInvoice[]
  /** Server-reported total (may exceed `invoices.length` — see the limit). */
  totalCount: number
}

/**
 * Fetch the actor's overdue INVOICES (quotes are excluded via `document_type`).
 * Sorted server-side by due date ascending so the oldest debt arrives first
 * even when the list is truncated at the limit.
 */
export async function fetchOverdueInvoices(
  limit: number = OVERDUE_FETCH_LIMIT,
): Promise<OverdueFetchResult> {
  const response = await api.get('/invoices', {
    params: {
      status: 'overdue',
      document_type: 'invoice',
      per_page: limit,
      page: 1,
      sort: 'due_date',
      dir: 'asc',
    },
  })
  const body = response?.data ?? {}
  const rows: OverdueInvoice[] = Array.isArray(body.data) ? body.data : Array.isArray(body) ? body : []
  const reported = Number(body?.meta?.pagination?.total_count)
  return {
    invoices: rows,
    totalCount: Number.isFinite(reported) ? reported : rows.length,
  }
}

/** Today as `YYYY-MM-DD` in the operator's own timezone — the default payment
 * date for a one-click "mark paid". */
export function todayForPayment(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

/** One-click settle. `paidDate` defaults to today. */
export async function markOverdueInvoicePaid(id: number, paidDate?: string): Promise<void> {
  await api.post(`/invoices/${id}/mark_as_paid`, { paid_date: paidDate || todayForPayment() })
}

/** Undo a mis-click from the drawer. */
export async function markOverdueInvoiceUnpaid(id: number): Promise<void> {
  await api.post(`/invoices/${id}/mark_as_unpaid`)
}

/** Broadcast so every mounted consumer (the banner, an open Invoices page)
 * re-reads after a payment lands. Fired by the drawer; listened to by
 * `useOverdueInvoices`. */
export const OVERDUE_INVOICES_CHANGED_EVENT = 'crm:overdue-invoices-changed'

export function notifyOverdueInvoicesChanged(): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(OVERDUE_INVOICES_CHANGED_EVENT))
}
